import { createSlice } from '@reduxjs/toolkit';
import { getPersistedDjSidebarWidth } from '../utils/djSidebarWidth';

const initialState = {
    sidebarOpen: false,
    sidebarWidth: getPersistedDjSidebarWidth(),
    syncStatus: 'not_synced', // 'not_synced' | 'checking' | 'synced' | 'partial' (import stopped/failed, whole playlists kept) | 'error'
    virtualdjPath: null,
    virtualdjExists: null,
    virtualdjPlaylists: [],
    lastSyncTime: null,
    importing: false,
    exporting: false,
    error: null,
    persistedStateLoaded: false
};

const virtualdjSlice = createSlice({
    name: 'virtualdj',
    initialState,
    reducers: {
        loadPersistedVirtualDJState(state, action) {
            if (state.persistedStateLoaded) return;

            if (action.payload) {
                const { lastSyncTime, virtualdjPath, virtualdjPlaylists } = action.payload;
                if (lastSyncTime) {
                    state.lastSyncTime = lastSyncTime;
                    state.syncStatus = 'synced';
                }
                if (virtualdjPath) {
                    state.virtualdjPath = virtualdjPath;
                }
                if (virtualdjPlaylists && virtualdjPlaylists.length > 0) {
                    state.virtualdjPlaylists = virtualdjPlaylists;
                }
            }

            state.persistedStateLoaded = true;
        },
        toggleVirtualDJSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setVirtualDJSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        setVirtualDJSidebarWidth(state, action) {
            state.sidebarWidth = action.payload;
        },
        setVirtualDJSyncStatus(state, action) {
            state.syncStatus = action.payload;
        },
        setVirtualDJPath(state, action) {
            state.virtualdjPath = action.payload;
        },
        setVirtualDJExists(state, action) {
            state.virtualdjExists = action.payload;
        },
        setVirtualDJPlaylists(state, action) {
            state.virtualdjPlaylists = action.payload;
        },
        setVirtualDJLastSyncTime(state, action) {
            state.lastSyncTime = action.payload;
        },
        setVirtualDJImporting(state, action) {
            state.importing = action.payload;
        },
        setVirtualDJExporting(state, action) {
            state.exporting = action.payload;
        },
        setVirtualDJError(state, action) {
            state.error = action.payload;
        },
        // Owner-scoped "Last synced" from the main process (djlibrary:getSyncState),
        // fetched on boot and on every account change by DjSyncStateHydrator.
        // Null = this account never synced this app: leave the state alone.
        hydrateVirtualDJSyncState(state, action) {
            const at = action.payload;
            if (!at) return;
            state.lastSyncTime = at;
            if (state.syncStatus === 'not_synced') state.syncStatus = 'synced';
        },
        resetVirtualDJState(state) {
            state.syncStatus = 'not_synced';
            state.virtualdjPlaylists = [];
            state.lastSyncTime = null;
            state.error = null;
        }
    },
});

export const {
    loadPersistedVirtualDJState,
    toggleVirtualDJSidebar,
    setVirtualDJSidebarOpen,
    setVirtualDJSidebarWidth,
    setVirtualDJSyncStatus,
    setVirtualDJPath,
    setVirtualDJExists,
    setVirtualDJPlaylists,
    setVirtualDJLastSyncTime,
    setVirtualDJImporting,
    setVirtualDJExporting,
    setVirtualDJError,
    hydrateVirtualDJSyncState,
    resetVirtualDJState
} = virtualdjSlice.actions;

export default virtualdjSlice.reducer;
